import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { settingsAPI } from '@/lib/api';
import { useTheme } from '@/context/ThemeContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logo, setLogo] = useState('https://customer-assets.emergentagent.com/job_career-destination/artifacts/pnqixpyi_arya_mohajer_asia_logo.png');
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsAPI.get();
        if (response.data.logo_url) {
          setLogo(response.data.logo_url);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const navLinks = [
    { name: 'صفحه اصلی', path: '/' },
    { name: 'کشورها', path: '/countries' },
    { name: 'دانشگاه\u200cها', path: '/universities' },
    { name: 'مدارس', path: '/schools' },
    { name: 'فرصت\u200cهای شغلی', path: '/jobs' },
    { name: 'خدمات', path: '/services' },
    { name: 'مقالات', path: '/articles' },
    { name: 'گالری سمینارها', path: '/gallery' },
    { name: 'درباره ما', path: '/about' },
    { name: 'تماس با ما', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 glass-effect border-b border-white/10" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <div className="relative overflow-hidden rounded-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_career-destination/artifacts/ysupexoy_WhatsApp%20Image%202026-07-12%20at%2016.22.11.jpeg"
                alt="ARYA MOHAJER ASIA" 
                className="h-16 w-auto object-contain transition-all duration-500 ease-out group-hover:scale-110 group-hover:brightness-110"
              />
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/0 via-[#D4AF37]/30 to-[#D4AF37]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
            </div>
            {/* Text that appears on hover */}
            <div className="overflow-hidden max-w-0 group-hover:max-w-xs transition-all duration-500">
              <span className="text-white font-semibold text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                ARYA MOHAJER ASIA
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.slice(1) || 'home'}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-[#D4AF37] text-black'
                    : 'text-zinc-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-3 rounded-full glass-effect border border-white/10 hover:border-[#D4AF37]/30 transition-all hover:scale-110"
              aria-label="تغییر تم"
            >
              {theme === 'dark' ? (
                <Sun size={20} className="text-[#D4AF37]" />
              ) : (
                <Moon size={20} className="text-[#D4AF37]" />
              )}
            </button>

            {/* CTA Button */}
            <Link
              to="/contact"
              data-testid="header-cta-button"
              className="px-6 py-3 bg-[#D4AF37] text-black rounded-full text-sm font-medium hover:bg-[#F3E5AB] transition-colors"
            >
              مشاوره رایگان
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden glass-effect border-t border-white/10" data-testid="mobile-menu">
          <nav className="flex flex-col px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-[#D4AF37] text-black'
                    : 'text-zinc-300 hover:bg-white/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Mobile Theme Toggle */}
            <button
              onClick={() => {
                toggleTheme();
                setMobileMenuOpen(false);
              }}
              className="px-4 py-3 rounded-lg text-sm font-medium transition-colors text-zinc-300 hover:bg-white/5 flex items-center justify-between"
            >
              <span>تغییر تم</span>
              {theme === 'dark' ? (
                <Sun size={20} className="text-[#D4AF37]" />
              ) : (
                <Moon size={20} className="text-[#D4AF37]" />
              )}
            </button>

            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="px-4 py-3 bg-[#D4AF37] text-black rounded-lg text-sm font-medium text-center"
            >
              مشاوره رایگان
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
